// src/authProvider.js
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const isTokenExpired = (token) => {
    try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const payload = JSON.parse(window.atob(base64));
        return payload.exp < Math.floor(Date.now() / 1000);
    } catch (error) {
        return true;
    }
};

const KEYS = ['token', 'refresh_token', 'roles', 'username', 'tenant_id', 'tenant_slug', 'is_super_admin', 'colaborator_id'];
const clearStorage = () => KEYS.forEach((k) => localStorage.removeItem(k));

// Decodeaza payload-ul JWT (base64url) - null daca token-ul e malformat.
const decodeJwt = (token) => {
    try {
        const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
        return JSON.parse(window.atob(base64));
    } catch (error) {
        return null;
    }
};

// Scrie identitatea din token in localStorage. Apelata si la login, si la refresh:
// altfel rolurile/tenantul raman inghetate la valorile de la ultimul login real,
// iar refresh token-ul tine sesiunea vie la nesfarsit - o schimbare de rol in DB
// nu s-ar vedea niciodata fara logout explicit.
export const storeIdentityFromToken = (token, fallbackUsername) => {
    const payload = decodeJwt(token);
    if (!payload) {
        console.error('Eroare decodare payload JWT');
        return;
    }
    localStorage.setItem('roles', JSON.stringify(payload.roles || []));
    localStorage.setItem('username', payload.username || fallbackUsername || '');
    localStorage.setItem('tenant_id', payload.tenant_id ?? '');
    localStorage.setItem('tenant_slug', payload.tenant_slug ?? '');
    localStorage.setItem('is_super_admin', payload.is_super_admin ? '1' : '0');
    localStorage.setItem('colaborator_id', payload.colaborator_id ?? '');
};

const tryRefreshToken = async () => {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) return false;

    try {
        const response = await fetch(`${API_URL}/token/refresh`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refresh_token: refreshToken }),
        });
        if (!response.ok) return false;

        const { token, refresh_token: newRefreshToken } = await response.json();
        if (!token || isTokenExpired(token)) return false;

        localStorage.setItem('token', token);
        if (newRefreshToken) localStorage.setItem('refresh_token', newRefreshToken);
        storeIdentityFromToken(token, localStorage.getItem('username'));
        return true;
    } catch {
        return false;
    }
};

export const authProvider = {
    login: async ({ username, password, slug }) => {
        const body = { email: username, password };
        if (slug && slug.trim()) body.slug = slug.trim();

        const response = await fetch(`${API_URL}/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });

        if (!response.ok) {
            const data = await response.json().catch(() => ({}));
            throw new Error(data.message || 'Email, parola sau organizatie incorecta');
        }

        const { token, refresh_token } = await response.json();
        if (isTokenExpired(token)) throw new Error('Token invalid primit de la server');

        localStorage.setItem('token', token);
        if (refresh_token) localStorage.setItem('refresh_token', refresh_token);

        storeIdentityFromToken(token, username);
    },

    logout: () => { clearStorage(); return Promise.resolve(); },

    checkAuth: async () => {
        const token = localStorage.getItem('token');
        if (token && !isTokenExpired(token)) return Promise.resolve();

        // JWT expirat - incercam refresh silent
        const refreshed = await tryRefreshToken();
        return refreshed ? Promise.resolve() : Promise.reject();
    },

    checkError: (error) => {
        const status = error?.status || error?.response?.status;
        if (status === 401 || status === 403) { clearStorage(); return Promise.reject(); }
        return Promise.resolve();
    },

    getIdentity: () => {
        const username = localStorage.getItem('username');
        const tenantSlug = localStorage.getItem('tenant_slug');
        const superAdmin = localStorage.getItem('is_super_admin') === '1';
        const fullName = superAdmin
            ? `${username} (superadmin)`
            : tenantSlug ? `${username} @ ${tenantSlug}` : username;
        return username ? Promise.resolve({ id: username, fullName }) : Promise.reject();
    },

    getPermissions: () => {
        const roles = localStorage.getItem('roles');
        return Promise.resolve(roles ? JSON.parse(roles) : []);
    },
};
